import { ChangeDetectionStrategy, Component, DestroyRef, ViewEncapsulation, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { FocusTarget } from './focus-target';

/** The path part of a router URL, without query string or fragment. */
export function routePath(url: string): string {
  return url.split(/[?#]/)[0];
}

/** The title of the deepest active route that defines one. */
export function deepestRouteTitle(root: ActivatedRouteSnapshot): string | null {
  let title: string | null = null;
  let node: ActivatedRouteSnapshot | null = root;
  while (node) {
    if (node.title) title = node.title;
    node = node.firstChild;
  }
  return title;
}

/**
 * Announces the page name after each move to another page and puts focus on
 * the page region, so keyboard and screen reader users start at the content.
 * Query-string changes (filters, paging) neither announce nor move focus.
 */
@Component({
  selector: 'app-route-announcer-cabinet',
  template: `<div class="announcer" aria-live="polite" aria-atomic="true">{{ message() }}</div>`,
  styles: [
    `
      .announcer {
        position: absolute;
        width: 1px;
        height: 1px;
        margin: -1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
      }
    `,
  ],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RouteAnnouncerCabinetComponent {
  /** Receives focus after navigation. */
  readonly target = input<FocusTarget | null>(null);
  /** Page name for routes without a title (desktop routes have none). */
  readonly fallbackTitle = input<(path: string) => string | null>(() => null);

  readonly message = signal('');

  private readonly router = inject(Router);
  // Null until the first navigation ends: the initial page load is not announced.
  private lastPath: string | null = this.router.navigated ? routePath(this.router.url) : null;

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(inject(DestroyRef)),
      )
      .subscribe((e) => this.onNavigated(e.urlAfterRedirects));
  }

  private onNavigated(url: string): void {
    const path = routePath(url);
    const previous = this.lastPath;
    this.lastPath = path;
    if (previous === null || previous === path) return;
    const title = deepestRouteTitle(this.router.routerState.snapshot.root) ?? this.fallbackTitle()(path);
    // Cleared first so the same name twice in a row is still announced.
    this.message.set('');
    setTimeout(() => this.message.set(title ?? ''), 50);
    this.target()?.focus();
  }
}
